import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Header } from '../../../../base/components/Header';
import { BreadcrumbLink, HeaderWithBreadcrumbs } from '../../../../base/components/HeaderWithBreadcrumbs';
import {
    useAppDispatch,
    useAppSelector,
    useBrowserBackClickHandler,
    useHideWebsiteFooter,
    useIsMobile,
} from '../../../../base/hooks';
import { __t } from '../../../../base/utils/t';
import { Helmet } from '../../../components/Helmet';
import { Footer } from '../components/Footer';
import { QuickApplyStep } from '../enums';
import { quickApplyActions } from '../redux/slices/quickApplySlice';
import { ContactInfo } from '../components/FormSteps/ContactInfo';
import { PersonalInfo } from '../components/FormSteps/PersonalInfo';
import { TextDescription } from '../components/FormSteps/TextDescription';
import { ApplySuccess } from '../components/ApplySuccess';
import { VerifyPhoneModal } from '../components/VerifyPhoneModal';
import { quickSignUpActions } from '../../authorization/redux/slices/quickSignUpSlice';

import './styles.scss';

export const QuickApplyPage = () => {
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { step, vacancyId } = useAppSelector((state) => state.workerAppQuickApply);
    const { referrer } = useAppSelector((state) => state.app);

    const [savedReferrerLocation, setSavedReferrerLocation] = useState('');
    const [showPhoneConfirmationModal, setShowPhoneConfirmationModal] = useState(false);

    const params = useParams<{ id: string }>();

    useHideWebsiteFooter();

    useEffect(() => {
        if(window.GLOBALS.workerId) {
            dispatch(quickApplyActions.setStep(QuickApplyStep.PersonalInfo));
            dispatch(quickApplyActions.getQuickApplyInfo());
        }
        if (params.id) {
            dispatch(quickApplyActions.setVacancyId(params.id));
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0 });

        if (!document.referrer) {
            navigate('');
        }

        return function () {
            dispatch(quickApplyActions.clearData());
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [step]);

    useEffect(() => {
        if (location.state?.from?.pathname) {
            setSavedReferrerLocation(location.state?.from?.pathname);
        }
    }, [location]);

    const onBackClick = useCallback(() => {
        if ([QuickApplyStep.PersonalInfo, QuickApplyStep.Success].includes(step) && vacancyId) {
            window.location.href = `/vacancy-${vacancyId}.html`;
            return;
        }

        if (step === QuickApplyStep.TextDescription) {
            dispatch(quickApplyActions.setStep(step - 1));
            return;
        }

        if (savedReferrerLocation) {
            navigate(savedReferrerLocation);
            return;
        }
        if (referrer) {
            window.location.href = referrer;
            return;
        }
        window.location.href = '/vacancies';
    }, [step, referrer, savedReferrerLocation]);

    useBrowserBackClickHandler(onBackClick);

    const progressWidth = useMemo(() => {
        switch (step) {
            case QuickApplyStep.ContactInfo:
                return 25;
            case QuickApplyStep.PersonalInfo:
                return 50;
            case QuickApplyStep.TextDescription:
                return 75;
            case QuickApplyStep.Success:
                return 100;
            default:
                return 0;
        }
    }, [step]);

    const hidePhoneConfirmationModalHandler = () => setShowPhoneConfirmationModal(false);

    const nextLinkText = useMemo(() => {
        if (step === QuickApplyStep.TextDescription) {
            return __t('Откликнуться');
        }

        return __t('Далее');
    }, [step]);

    const onNextClick = () => {
        if (step === QuickApplyStep.ContactInfo) {
            dispatch(quickSignUpActions.validateQuickSignUpParams(contactDataValidationPassedCallback));
            return;
        }
        dispatch(quickApplyActions.submit());
    };

    const breadcrumbs: BreadcrumbLink[] = [
        {
            text: __t('Главная'),
            url: '/',
        },
        {
            text: __t('Вакансии'),
            url: '/vacancies',
        },
    ];

    const contactDataValidationPassedCallback = () => {
        setShowPhoneConfirmationModal(true);
    };

    const pageContent = useMemo(() => {
        switch (step) {
            case QuickApplyStep.ContactInfo:
                return <ContactInfo />;
            case QuickApplyStep.PersonalInfo:
                return <PersonalInfo />;
            case QuickApplyStep.TextDescription:
                return <TextDescription />;
            case QuickApplyStep.Success:
                return <ApplySuccess />;
            default:
                return null;
        }
    }, [step]);

    return (
        <>
            <Helmet pageName={__t('Отклик без анкеты')} />
            <div className='quick-apply-page'>
                {isMobile ? (
                    <Header
                        title={step === QuickApplyStep.Success ? __t('Назад к вакансии') : __t('Отклик без анкеты')}
                        onBackClick={onBackClick}
                    />
                ) : (
                    <HeaderWithBreadcrumbs
                        links={breadcrumbs}
                        current={__t('Отклик без анкеты')}
                        title={__t('Отклик без анкеты')}
                        backLinkText={__t('Назад к вакансии')}
                        onBackClick={step === QuickApplyStep.Success ? onBackClick : undefined}
                    />
                )}
                <div className='razem-app__wrapper'>
                    <div className='quick-apply-page__content'>{pageContent}</div>
                </div>
                {step !== QuickApplyStep.Success && (
                    <Footer
                        backLinkText={step === QuickApplyStep.PersonalInfo ? __t('Выйти') : __t('Назад')}
                        nextLinkText={nextLinkText}
                        progressWidth={progressWidth}
                        hideNextIcon={step === QuickApplyStep.TextDescription}
                        onBackClick={onBackClick}
                        onNextClick={onNextClick}
                    />
                )}
            </div>
            {step === QuickApplyStep.ContactInfo && (
                <VerifyPhoneModal
                    showModal={showPhoneConfirmationModal}
                    hideModalHandler={hidePhoneConfirmationModalHandler}
                />
            )}
        </>
    );
};
